// ============================================
// PAYMENT HANDLING
// Handles both Stripe (production) and demo mode
// ============================================
import { loadStripe } from '@stripe/stripe-js/pure';
import { isSupabaseConfigured, supabase, sendRestoreCode, verifyRestoreCode, verifyProStatusSecure } from './supabase.js';

// Configuration
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const isStripeConfigured = stripePublishableKey && stripePublishableKey.startsWith('pk_');

// Plan configuration (must match backend)
export const PLANS = {
    single_move: {
        id: 'single_move',
        name: 'Single Move',
        price: 999, // cents
        priceDisplay: '$9.99',
        description: '60 days of unlimited access',
        durationDays: 60,
    },
    pro_annual: {
        id: 'pro_annual',
        name: 'Pro Annual',
        price: 2999, // cents
        priceDisplay: '$29.99',
        description: '1 year of unlimited access',
        durationDays: 365,
    },
};

// Current selected plan
let selectedPlanId = 'single_move';

// Stripe instance
let stripe = null;
let elements = null;
let cardElement = null;

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialize Stripe if configured
 */
export async function initStripe() {
    if (!isStripeConfigured) {
        console.log('Stripe not configured - running in demo mode');
        return false;
    }

    try {
        stripe = await loadStripe(stripePublishableKey);
        console.log('Stripe initialized');
        return true;
    } catch (error) {
        console.error('Failed to initialize Stripe:', error);
        return false;
    }
}

/**
 * Check if we're in production mode (Stripe + Supabase configured)
 */
export function isProductionMode() {
    return isStripeConfigured && isSupabaseConfigured();
}

// ============================================
// PLAN SELECTION
// ============================================

/**
 * Select a plan and open the payment modal
 */
export function selectPlan(planId) {
    if (!PLANS[planId]) {
        console.error('Invalid plan:', planId);
        return;
    }

    selectedPlanId = planId;
    const plan = PLANS[planId];

    // Update payment modal with plan details
    const nameEl = document.getElementById('orderPlanName');
    const priceEl = document.getElementById('orderPlanPrice');
    const descEl = document.getElementById('orderPlanDesc');
    const totalEl = document.getElementById('orderTotal');
    const hiddenEl = document.getElementById('selectedPlan');

    if (nameEl) nameEl.textContent = plan.name;
    if (priceEl) priceEl.textContent = plan.priceDisplay;
    if (descEl) descEl.textContent = plan.description;
    if (totalEl) totalEl.textContent = `${plan.priceDisplay} USD`;
    if (hiddenEl) hiddenEl.value = planId;

    // Update pay button text
    const payButtonText = document.getElementById('payButtonText');
    if (payButtonText) payButtonText.textContent = `Pay ${plan.priceDisplay}`;

    // Show/hide demo notice based on production mode
    const demoNotice = document.getElementById('demoNotice');
    if (demoNotice) {
        demoNotice.style.display = isProductionMode() ? 'none' : 'flex';
    }

    // Close upgrade modal and open payment modal
    if (typeof window.closeModal === 'function') {
        window.closeModal('upgradeModal');
    }
    if (typeof window.showModal === 'function') {
        window.showModal('paymentModal');
    }

    // Swap the card fields for the current mode (Stripe Elements vs demo)
    configurePaymentForm();
}

/**
 * Configure the payment form for the current mode.
 * Production: mount Stripe Elements (PCI-compliant iframe) and hide the
 * demo card/billing inputs — raw card data must never touch our DOM.
 * Demo: show the simulated inputs, which only accept Stripe test cards.
 */
export async function configurePaymentForm() {
    const demoFields = document.getElementById('demoCardFields');
    const billingFields = document.getElementById('billingFields');
    const stripeFields = document.getElementById('stripeCardFields');

    // Hidden inputs with `required` would block form submission, so
    // toggle the attribute along with visibility (remembering originals).
    const setRequired = (root, on) => {
        if (!root) return;
        root.querySelectorAll('input, select').forEach((el) => {
            if (on) {
                if (el.dataset.wasRequired === 'true') el.required = true;
            } else if (el.required) {
                el.dataset.wasRequired = 'true';
                el.required = false;
            }
        });
    };

    if (isProductionMode()) {
        if (demoFields) demoFields.style.display = 'none';
        if (billingFields) billingFields.style.display = 'none';
        setRequired(demoFields, false);
        setRequired(billingFields, false);
        if (stripeFields) stripeFields.style.display = 'block';
        if (!cardElement) {
            await setupStripeCard();
        }
    } else {
        if (demoFields) demoFields.style.display = '';
        if (billingFields) billingFields.style.display = '';
        setRequired(demoFields, true);
        setRequired(billingFields, true);
        if (stripeFields) stripeFields.style.display = 'none';
    }
}

/**
 * Get the currently selected plan
 */
export function getSelectedPlan() {
    return PLANS[selectedPlanId] || PLANS.single_move;
}

/**
 * Get selected plan ID
 */
export function getSelectedPlanId() {
    return selectedPlanId;
}

// ============================================
// PAYMENT FORM HANDLING
// ============================================

/**
 * Setup Stripe Elements card input
 * Call this when the payment modal opens
 */
export async function setupStripeCard(containerId = 'stripe-card-element') {
    if (!stripe) {
        await initStripe();
    }

    if (!stripe) {
        console.log('Stripe not available, using demo mode');
        return null;
    }

    const container = document.getElementById(containerId);
    if (!container) {
        console.error('Stripe card container not found:', containerId);
        return null;
    }

    // Create Elements instance
    elements = stripe.elements({
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#2f7f8a',
                colorBackground: '#ffffff',
                colorText: '#2b2722',
                colorDanger: '#c25040',
                fontFamily: 'DM Sans, system-ui, sans-serif',
                borderRadius: '10px',
                spacingUnit: '4px',
            },
            rules: {
                '.Input': {
                    border: '1px solid #e6dfd4',
                    boxShadow: 'none',
                },
                '.Input:focus': {
                    border: '1px solid #2f7f8a',
                    boxShadow: '0 0 0 3px rgba(47, 127, 138, 0.2)',
                },
            },
        },
    });

    // Create and mount card element
    cardElement = elements.create('card', {
        style: {
            base: {
                fontSize: '16px',
                color: '#2b2722',
                '::placeholder': {
                    color: '#8a8174',
                },
            },
            invalid: {
                color: '#c25040',
            },
        },
        hidePostalCode: false, // We'll collect it in the billing section
    });

    cardElement.mount(container);

    // Listen for errors
    cardElement.on('change', (event) => {
        const errorEl = document.getElementById('paymentError');
        if (event.error) {
            errorEl.textContent = event.error.message;
            errorEl.style.display = 'block';
        } else {
            errorEl.style.display = 'none';
        }
    });

    return cardElement;
}

/**
 * Process payment - handles both Stripe and demo mode
 */
export async function processPayment(email, paymentData) {
    if (isProductionMode()) {
        // Never fall back to demo in production: failing closed beats
        // showing a fake "success" for what the user thinks is a purchase.
        return processStripePayment(email, paymentData?.name);
    }
    return processDemoPayment(paymentData);
}

/**
 * Process payment with Stripe
 */
async function processStripePayment(email, cardholderName) {
    if (!stripe || !cardElement) {
        throw new Error('Payment form is still loading. Please close this window and try again.');
    }

    const planType = getSelectedPlanId();
    const plan = getSelectedPlan();

    // Create PaymentIntent via Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: { email, planType },
    });

    if (error) {
        throw new Error(error.message || 'Failed to create payment');
    }

    const { clientSecret, customerId, expiresAt } = data;

    // Confirm payment with Stripe
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
            card: cardElement,
            billing_details: {
                email: email,
                ...(cardholderName ? { name: cardholderName } : {}),
            },
        },
    });

    if (stripeError) {
        throw new Error(stripeError.message);
    }

    if (paymentIntent.status === 'succeeded') {
        return {
            success: true,
            paymentIntentId: paymentIntent.id,
            customerId: customerId,
            planType: planType,
            planName: plan.name,
            expiresAt: expiresAt,
        };
    }

    throw new Error('Payment was not completed');
}

/**
 * Process payment in demo mode (for testing without Stripe)
 */
async function processDemoPayment(paymentData) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Test card numbers for demo
    const testCards = {
        '4242424242424242': { success: true },
        '4000000000000002': { success: false, error: 'Your card was declined.' },
        '4000000000009995': { success: false, error: 'Insufficient funds.' },
        '4000000000000069': { success: false, error: 'Card expired.' },
    };

    const cardNumber = paymentData.cardNumber.replace(/\s/g, '');
    const testResult = testCards[cardNumber];

    // Only recognized Stripe test cards are accepted in demo mode.
    // Rejecting everything else keeps people from typing a real card
    // number into a form that never actually charges anything.
    if (!testResult) {
        throw new Error('Demo mode: use test card 4242 4242 4242 4242. Never enter a real card number here.');
    }

    if (!testResult.success) {
        throw new Error(testResult.error);
    }

    // Calculate expiration for demo
    const plan = getSelectedPlan();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + plan.durationDays);

    return {
        success: true,
        demo: true,
        planType: getSelectedPlanId(),
        planName: plan.name,
        expiresAt: expiresAt.toISOString(),
    };
}

// ============================================
// RESTORE PURCHASE
// ============================================
// Restoring is a two-step flow: we email a one-time code to prove the
// requester owns the address, and only check for a purchase after the
// code is verified. Unverified requests never learn whether a purchase
// exists for an email.

/**
 * Step 1: request a verification code for the email.
 * A code is sent regardless of whether a purchase exists.
 */
export async function requestRestoreCode(email) {
    if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email address');
    }

    if (!isSupabaseConfigured()) {
        throw new Error('Restore is unavailable in demo mode. Use demo@test.com to simulate.');
    }

    await sendRestoreCode(email);
    return { sent: true, email };
}

/**
 * Step 2: verify the code, then check Pro status with the
 * authenticated session that verification creates.
 */
export async function confirmRestoreCode(email, code) {
    if (!code || code.trim().length < 6) {
        throw new Error('Please enter the 6-digit code from your email');
    }

    // Proves ownership of the email (throws on bad/expired code)
    await verifyRestoreCode(email, code.trim());

    // Now safe to check purchase status for this verified identity
    const status = await verifyProStatusSecure();
    if (status.isPro) {
        return { success: true, email, expiresAt: status.expiresAt, planType: status.planType };
    }

    throw new Error('Your email is verified, but no active purchase was found for it.');
}

// ============================================
// FORM VALIDATION (for demo mode)
// ============================================

/**
 * Validate card form data (demo mode only)
 */
export function validateCardForm() {
    const cardNumber = document.getElementById('cardNumber')?.value.replace(/\s/g, '') || '';
    const expiry = document.getElementById('cardExpiry')?.value.replace(/\s/g, '') || '';
    const cvc = document.getElementById('cardCvc')?.value || '';
    const name = document.getElementById('cardName')?.value || '';
    const email = document.getElementById('paymentEmail')?.value || '';
    const country = document.getElementById('billingCountry')?.value || '';
    const zip = document.getElementById('billingZip')?.value || '';

    // Basic validation
    if (!email || !email.includes('@')) {
        return { valid: false, error: 'Please enter a valid email address.' };
    }

    if (cardNumber.length < 15) {
        return { valid: false, error: 'Please enter a valid card number.' };
    }

    // Luhn check
    if (!luhnCheck(cardNumber)) {
        return { valid: false, error: 'Invalid card number. Please check and try again.' };
    }

    if (expiry.length < 4) {
        return { valid: false, error: 'Please enter a valid expiration date.' };
    }

    // Check expiry is in future
    const [month, year] = expiry.split('/');
    const expMonth = parseInt(month);
    const expYear = parseInt('20' + year);
    const now = new Date();

    if (expMonth < 1 || expMonth > 12) {
        return { valid: false, error: 'Invalid expiration month.' };
    }

    if (expYear < now.getFullYear() || (expYear === now.getFullYear() && expMonth < now.getMonth() + 1)) {
        return { valid: false, error: 'Card has expired.' };
    }

    if (cvc.length < 3) {
        return { valid: false, error: 'Please enter a valid CVC code.' };
    }

    if (!name.trim()) {
        return { valid: false, error: 'Please enter the name on your card.' };
    }

    if (!country) {
        return { valid: false, error: 'Please select your country.' };
    }

    if (!zip.trim()) {
        return { valid: false, error: 'Please enter your ZIP/postal code.' };
    }

    return {
        valid: true,
        data: {
            email,
            cardNumber,
            expMonth,
            expYear,
            cvc,
            name,
            country,
            zip,
        },
    };
}

/**
 * Luhn algorithm for card number validation
 */
function luhnCheck(cardNumber) {
    let sum = 0;
    let isEven = false;

    for (let i = cardNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cardNumber[i]);

        if (isEven) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }

        sum += digit;
        isEven = !isEven;
    }

    return sum % 10 === 0;
}

// ============================================
// EXPORTS FOR WINDOW
// ============================================
window.initStripe = initStripe;
window.isProductionMode = isProductionMode;
window.setupStripeCard = setupStripeCard;
window.configurePaymentForm = configurePaymentForm;
window.selectPlan = selectPlan;
window.getSelectedPlan = getSelectedPlan;
window.PLANS = PLANS;
